import PptxGenJS from 'pptxgenjs';
import { hasPeerValue, metricText, metricComparison, scorecardStats } from './scorecard';
import { appendQualitySlide } from './reportQualityExport';

// Brand colors
const COLORS = {
  primary: '059669',      // emerald-600
  primaryDark: '047857',  // emerald-700
  secondary: '0D9488',    // teal-600
  text: '1F2937',         // gray-800
  textLight: '6B7280',    // gray-500
  white: 'FFFFFF',
  red: 'DC2626',
  amber: 'D97706',
  blue: '2563EB',
  purple: '7C3AED',
  indigo: '4F46E5',
  orange: 'EA580C',
  green: '16A34A',
  cyan: '0891B2',
  yellow: 'CA8A04',
  lightGray: 'F3F4F6',
};

// Helper to format currency
const formatCurrency = (value, sym = '$') => {
  if (value == null) return 'Not available';
  if (typeof value === 'string') return value;
  if (value >= 1000000) return `${sym}${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${sym}${(value / 1000).toFixed(0)}K`;
  return `${sym}${value}`;
};

// Helper to safely convert value to string with max length
const safeStr = (val, maxLen = 200) => {
  if (!val) return '';
  if (typeof val === 'string') return val.substring(0, maxLen);
  if (Array.isArray(val)) return val.join(', ').substring(0, maxLen);
  return String(val).substring(0, maxLen);
};

// Helper to format units - converts 'percentage', 'Percentage', 'percent' to '%'
const formatUnit = (unit) => {
  if (!unit) return '';
  const lowerUnit = unit.toLowerCase();
  if (lowerUnit === 'percentage' || lowerUnit === 'percent') return '%';
  if (unit === '%' || unit === 'x') return unit;
  return ` ${unit}`;
};

// Helper to check if lower is better for a KPI
const isLowerBetter = (kpiName) => {
  const name = kpiName.toLowerCase();
  return name.includes('cost') || name.includes('churn') || name.includes('turnover') ||
         name.includes('incident') || name.includes('downtime') || name.includes('defect') ||
         name.includes('error') || name.includes('emission') || name.includes('cac') ||
         name.includes('attrition') || name.includes('debt') || name.includes('lead time');
};

export const generatePptx = async (report, analysis, companyName, userName) => {
  const pres = new PptxGenJS();
  
  // Set presentation properties
  pres.layout = 'LAYOUT_16x9';
  pres.title = `${companyName} Value Scorecard`;
  pres.author = 'SAMANVAI';
  pres.company = 'SAMANVAI';
  pres.subject = 'Executive Intelligence Report';
  
  // Define master slide
  pres.defineSlideMaster({
    title: 'SAMANVAI_MASTER',
    background: { color: COLORS.white },
    objects: [
      { rect: { x: 0, y: 5.25, w: '100%', h: 0.25, fill: { color: COLORS.primary } } },
      { text: { text: 'SAMANVAI', options: { x: 0.3, y: 5.3, w: 1.5, h: 0.2, fontSize: 8, color: COLORS.white, bold: true } } },
    ],
  });
  
  const formatDate = () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  // Extract data
  const kpiData = analysis?.kpis || [];
  const peerComparison = (analysis?.peer_comparison || []).filter((pc) => [pc.peer1Value, pc.peer2Value, pc.peer3Value].some(hasPeerValue));
  const peers = analysis?.peers || [];
  const CUR = { USD: '$', EUR: '€', GBP: '£', INR: '₹' }[analysis?.data_governance?.currency || analysis?.currency || 'USD'] || '$';
  const fiveYearRoadmap = analysis?.five_year_roadmap || [];
  const areasOfImprovement = analysis?.areas_of_improvement || [];
  
  const { kpisAboveAvg, kpisBelowAvg, totalPotentialImpact, comparableCount, impactCount } = scorecardStats(kpiData, isLowerBetter);

  // ===== SLIDE 1: Cover Slide =====
  const slide1 = pres.addSlide();
  slide1.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: '100%', fill: { type: 'solid', color: COLORS.primary } });
  slide1.addShape(pres.ShapeType.rect, { x: 7, y: 0, w: 3.33, h: '100%', fill: { color: COLORS.primaryDark, transparency: 80 } });
  
  // Logo placeholder
  slide1.addShape(pres.ShapeType.roundRect, { x: 0.5, y: 0.4, w: 0.6, h: 0.6, fill: { color: COLORS.white, transparency: 80 }, rectRadius: 0.1 });
  slide1.addText('S', { x: 0.5, y: 0.4, w: 0.6, h: 0.6, fontSize: 24, bold: true, color: COLORS.white, align: 'center', valign: 'middle' });
  slide1.addText('SAMANVAI', { x: 1.2, y: 0.5, w: 2, h: 0.4, fontSize: 18, bold: true, color: COLORS.white });
  
  // Title
  slide1.addText(companyName, { x: 0.5, y: 2.2, w: 6, h: 0.8, fontSize: 40, bold: true, color: COLORS.white });
  slide1.addText('Value Scorecard', { x: 0.5, y: 2.9, w: 6, h: 0.6, fontSize: 28, color: 'A7F3D0' }); // emerald-200
  slide1.addText('Comprehensive Performance Analysis & Strategic Roadmap', { x: 0.5, y: 3.5, w: 6, h: 0.4, fontSize: 14, color: COLORS.white, transparency: 20 });
  
  // Info boxes at bottom
  const infoY = 4.5;
  slide1.addText('Industry', { x: 0.5, y: infoY, w: 2, h: 0.2, fontSize: 10, color: COLORS.white, transparency: 50 });
  slide1.addText(report?.sector || analysis?.sector || 'N/A', { x: 0.5, y: infoY + 0.2, w: 2, h: 0.3, fontSize: 12, bold: true, color: COLORS.white });
  
  slide1.addText('Prepared For', { x: 2.8, y: infoY, w: 2, h: 0.2, fontSize: 10, color: COLORS.white, transparency: 50 });
  slide1.addText(userName, { x: 2.8, y: infoY + 0.2, w: 2, h: 0.3, fontSize: 12, bold: true, color: COLORS.white });
  
  slide1.addText('Date', { x: 5.1, y: infoY, w: 2, h: 0.2, fontSize: 10, color: COLORS.white, transparency: 50 });
  slide1.addText(formatDate(), { x: 5.1, y: infoY + 0.2, w: 2, h: 0.3, fontSize: 12, bold: true, color: COLORS.white });

  // ===== SLIDE 2: Executive Summary =====
  const slide2 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
  slide2.addText('Executive Summary', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
  slide2.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
  
  const execSummary = report?.executive_summary || analysis?.executive_summary || 'Executive summary not available.';
  slide2.addText(execSummary.substring(0, 1200), { x: 0.5, y: 1.1, w: 9, h: 3.5, fontSize: 12, color: COLORS.text, valign: 'top', wrap: true });
  
  // Key metrics at bottom
  if (report?.projected_improvements) {
    const improvements = Object.entries(report.projected_improvements).slice(0, 5);
    const metricWidth = 1.7;
    improvements.forEach(([key, value], idx) => {
      slide2.addShape(pres.ShapeType.roundRect, { x: 0.5 + idx * metricWidth + idx * 0.1, y: 4.3, w: metricWidth, h: 0.8, fill: { color: 'ECFDF5' }, rectRadius: 0.1 });
      slide2.addText(value.toString(), { x: 0.5 + idx * metricWidth + idx * 0.1, y: 4.35, w: metricWidth, h: 0.4, fontSize: 16, bold: true, color: COLORS.primary, align: 'center' });
      slide2.addText(key.replace(/_/g, ' '), { x: 0.5 + idx * metricWidth + idx * 0.1, y: 4.7, w: metricWidth, h: 0.3, fontSize: 8, color: COLORS.textLight, align: 'center' });
    });
  }

  // ===== SLIDE 3: Performance Overview =====
  const slide3 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
  slide3.addText('Performance Overview', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
  slide3.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
  
  // 4 metric boxes
  const metrics = [
    { label: 'KPIs Above Average', value: String(kpisAboveAvg ?? 'N/A'), color: COLORS.primary, bgColor: 'ECFDF5' },
    { label: 'Need Attention', value: String(kpisBelowAvg ?? 'N/A'), color: COLORS.amber, bgColor: 'FFFBEB' },
    { label: '1% Improvement Value', value: formatCurrency(totalPotentialImpact, CUR), color: COLORS.purple, bgColor: 'FAF5FF' },
    { label: 'KPIs Analyzed', value: kpiData.length.toString(), color: COLORS.blue, bgColor: 'EFF6FF' },
  ];
  
  metrics.forEach((m, idx) => {
    const boxX = 0.5 + idx * 2.35;
    slide3.addShape(pres.ShapeType.roundRect, { x: boxX, y: 1.2, w: 2.2, h: 1.4, fill: { color: m.bgColor }, rectRadius: 0.1 });
    slide3.addText(m.value, { x: boxX, y: 1.5, w: 2.2, h: 0.6, fontSize: 32, bold: true, color: m.color, align: 'center' });
    slide3.addText(m.label, { x: boxX, y: 2.15, w: 2.2, h: 0.35, fontSize: 10, color: COLORS.textLight, align: 'center' });
  });
  
  // Performance summary text
  slide3.addText(`${companyName}: ${comparableCount} comparable KPIs; ${kpisAboveAvg ?? 'N/A'} above average and ${kpisBelowAvg ?? 'N/A'} below average. 1% improvement impact: ${formatCurrency(totalPotentialImpact, CUR)} (${impactCount} of ${kpiData.length} KPIs quantified). Missing values are not zero.`,
    { x: 0.5, y: 3, w: 9, h: 1, fontSize: 12, color: COLORS.text, wrap: true });

  // ===== SLIDE 4: KPI Analysis =====
  if (kpiData.length > 0) {
    const slide4 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide4.addText('KPI Analysis', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide4.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    // Legend
    slide4.addShape(pres.ShapeType.ellipse, { x: 0.5, y: 1.0, w: 0.15, h: 0.15, fill: { color: COLORS.red } });
    slide4.addText('Underperforming', { x: 0.7, y: 0.97, w: 1.2, h: 0.2, fontSize: 9, color: COLORS.red });
    slide4.addShape(pres.ShapeType.ellipse, { x: 2, y: 1.0, w: 0.15, h: 0.15, fill: { color: COLORS.primary } });
    slide4.addText('Above Average', { x: 2.2, y: 0.97, w: 1.2, h: 0.2, fontSize: 9, color: COLORS.primary });
    
    // Table header
    const tableRows = [
      [
        { text: 'KPI', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9 } },
        { text: 'Your Value', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'center' } },
        { text: 'Industry Avg', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'center' } },
        { text: 'Top Performer', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'center' } },
        { text: '1% Impact', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'right' } },
        { text: 'Basis · Conf.', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'center' } },
      ]
    ];
    
    kpiData.slice(0, 8).forEach((kpi) => {
      const lowerIsBetter = isLowerBetter(kpi.name);
      const comparison = metricComparison(kpi, lowerIsBetter);
      const isAboveAvg = comparison === null || comparison >= 0;
      const valueColor = isAboveAvg ? COLORS.primary : COLORS.red;
      const bgColor = isAboveAvg ? COLORS.white : 'FEF2F2';
      
      tableRows.push([
        { text: kpi.name, options: { fontSize: 9, color: COLORS.text, fill: { color: bgColor } } },
        { text: metricText(kpi.yourValue, formatUnit(kpi.unit)), options: { fontSize: 9, color: comparison === null ? COLORS.text : valueColor, bold: true, align: 'center', fill: { color: bgColor } } },
        { text: metricText(kpi.industryAvg, formatUnit(kpi.unit)), options: { fontSize: 9, color: COLORS.textLight, align: 'center', fill: { color: bgColor } } },
        { text: metricText(kpi.topPerformer, formatUnit(kpi.unit)), options: { fontSize: 9, color: COLORS.amber, align: 'center', fill: { color: bgColor } } },
        { text: formatCurrency(kpi.impactPer1Percent, CUR), options: { fontSize: 9, color: COLORS.text, align: 'right', fill: { color: bgColor } } },
        { text: kpi.basis ? `${kpi.basis}${kpi.confidence ? ` · ${kpi.confidence}` : ''}` : '—', options: { fontSize: 8, color: COLORS.textLight, align: 'center', fill: { color: bgColor } } },
      ]);
    });
    
    slide4.addTable(tableRows, {
      x: 0.5, y: 1.25, w: 9, h: 3.5,
      colW: [2.6, 1.2, 1.2, 1.3, 1.2, 1.5],
      border: { type: 'solid', pt: 0.5, color: 'E5E7EB' },
      fontFace: 'Arial',
    });
    if (kpiData.some((k) => k.basis)) {
      slide4.addText('Basis: reported = from company disclosure · derived = calculated · estimated = benchmark-based · modelled = scenario output. Conf. = confidence grade.', { x: 0.5, y: 4.95, w: 9, h: 0.3, fontSize: 8, italic: true, color: COLORS.textLight });
    }
  }

  // ===== SLIDE 5: KPI Improvement Values =====
  if (report?.kpi_improvement_values?.length > 0) {
    const slide5 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide5.addText('KPI Improvement Values', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide5.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    const kpiTableRows = [
      [
        { text: 'KPI', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9 } },
        { text: 'Current', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'center' } },
        { text: 'Target', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'center' } },
        { text: 'Annual Impact', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9, align: 'right' } },
        { text: '3-Year Value', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.primary, fontSize: 9, align: 'right' } },
      ]
    ];
    
    report.kpi_improvement_values.slice(0, 8).forEach((kpi) => {
      kpiTableRows.push([
        { text: kpi.kpi_name, options: { fontSize: 9, color: COLORS.text } },
        { text: kpi.current_value, options: { fontSize: 9, color: COLORS.textLight, align: 'center' } },
        { text: kpi.target_value, options: { fontSize: 9, color: COLORS.blue, align: 'center' } },
        { text: kpi.annual_value_impact, options: { fontSize: 9, color: COLORS.text, align: 'right' } },
        { text: kpi.three_year_value_impact, options: { fontSize: 9, color: COLORS.primary, bold: true, align: 'right' } },
      ]);
    });
    
    slide5.addTable(kpiTableRows, {
      x: 0.5, y: 1.1, w: 9, h: 3.5,
      colW: [3, 1.5, 1.5, 1.5, 1.5],
      border: { type: 'solid', pt: 0.5, color: 'E5E7EB' },
      fontFace: 'Arial',
    });
  }

  // ===== SLIDE 6: Peer Comparison =====
  if (peers.length > 0 && peerComparison.length > 0) {
    const slide6 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide6.addText('Peer Comparison', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide6.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    slide6.addText(`Comparing ${companyName} with: ${peers.join(', ')}`, { x: 0.5, y: 1.0, w: 9, h: 0.3, fontSize: 10, color: COLORS.textLight });
    
    const peerTableHeader = [
      { text: 'KPI', options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.textLight, fontSize: 9 } },
      { text: companyName.slice(0, 12), options: { bold: true, fill: { color: 'ECFDF5' }, color: COLORS.primary, fontSize: 9, align: 'center' } },
    ];
    if (peers[0]) peerTableHeader.push({ text: peers[0].slice(0, 12), options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.blue, fontSize: 9, align: 'center' } });
    if (peers[1]) peerTableHeader.push({ text: peers[1].slice(0, 12), options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.purple, fontSize: 9, align: 'center' } });
    if (peers[2]) peerTableHeader.push({ text: peers[2].slice(0, 12), options: { bold: true, fill: { color: COLORS.lightGray }, color: COLORS.orange, fontSize: 9, align: 'center' } });
    
    const peerTableRows = [peerTableHeader];
    
    peerComparison.slice(0, 8).forEach((pc) => {
      const row = [
        { text: pc.name, options: { fontSize: 9, color: COLORS.text } },
        { text: metricText(pc.yourValue, formatUnit(pc.unit)), options: { fontSize: 9, color: COLORS.primary, bold: true, align: 'center', fill: { color: 'ECFDF5' } } },
      ];
      if (peers[0]) row.push({ text: pc.peer1Value != null ? `${pc.peer1Value}${formatUnit(pc.unit)}` : '-', options: { fontSize: 9, color: COLORS.textLight, align: 'center' } });
      if (peers[1]) row.push({ text: pc.peer2Value != null ? `${pc.peer2Value}${formatUnit(pc.unit)}` : '-', options: { fontSize: 9, color: COLORS.textLight, align: 'center' } });
      if (peers[2]) row.push({ text: pc.peer3Value != null ? `${pc.peer3Value}${formatUnit(pc.unit)}` : '-', options: { fontSize: 9, color: COLORS.textLight, align: 'center' } });
      peerTableRows.push(row);
    });
    
    const colWidths = [3, 1.8];
    if (peers[0]) colWidths.push(1.4);
    if (peers[1]) colWidths.push(1.4);
    if (peers[2]) colWidths.push(1.4);
    
    slide6.addTable(peerTableRows, {
      x: 0.5, y: 1.4, w: 9, h: 3.5,
      colW: colWidths,
      border: { type: 'solid', pt: 0.5, color: 'E5E7EB' },
      fontFace: 'Arial',
    });
    const compTags = Array.from(new Set(peerComparison.map((pc) => pc.comparability).filter(Boolean)));
    if (compTags.length > 0) {
      slide6.addText(`Peer values are AI estimates unless marked reported. Comparability: ${compTags.join(', ')} — comparable = like-for-like · adjusted = normalised for scale/mix · directional = indicative only.`, { x: 0.5, y: 4.95, w: 9, h: 0.3, fontSize: 8, italic: true, color: COLORS.textLight });
    }
  }

  // ===== SLIDE 7: Financial Impact Summary =====
  if (report?.financial_impact_summary) {
    const slide7 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide7.addText('Financial Impact Summary', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide7.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    // Main value box
    slide7.addShape(pres.ShapeType.roundRect, { x: 2, y: 1.1, w: 6, h: 1.3, fill: { color: COLORS.primary }, rectRadius: 0.15 });
    slide7.addText('TOTAL BUSINESS VALUE POTENTIAL', { x: 2, y: 1.2, w: 6, h: 0.3, fontSize: 10, color: COLORS.white, align: 'center', transparency: 20 });
    slide7.addText(report.financial_impact_summary.total_business_value || 'N/A', { x: 2, y: 1.5, w: 6, h: 0.6, fontSize: 36, bold: true, color: COLORS.white, align: 'center' });
    slide7.addText('Over 3-Year Implementation Period', { x: 2, y: 2.1, w: 6, h: 0.2, fontSize: 9, color: COLORS.white, align: 'center', transparency: 30 });
    
    // Impact breakdown boxes (MECE categories — legacy fields shown only when present)
    const impactMetrics = [
      { label: 'Cost Savings', value: report.financial_impact_summary.total_potential_savings, color: COLORS.green },
      { label: 'Revenue Uplift', value: report.financial_impact_summary.revenue_uplift_potential, color: COLORS.blue },
      { label: 'Cost Reduction', value: report.financial_impact_summary.cost_reduction_potential, color: COLORS.purple },
      { label: 'Efficiency Gains', value: report.financial_impact_summary.efficiency_gains_value, color: COLORS.amber },
    ].filter((m) => m.value);
    
    const boxSpacing = 2.35;
    const startX = 5 - (impactMetrics.length * boxSpacing - 0.15) / 2;
    impactMetrics.forEach((m, idx) => {
      const boxX = startX + idx * boxSpacing;
      slide7.addShape(pres.ShapeType.roundRect, { x: boxX, y: 2.7, w: 2.2, h: 1, fill: { color: COLORS.white }, line: { color: 'E5E7EB', pt: 1 }, rectRadius: 0.1 });
      slide7.addText(m.label, { x: boxX, y: 2.8, w: 2.2, h: 0.3, fontSize: 9, color: COLORS.textLight, align: 'center' });
      slide7.addText(m.value || 'N/A', { x: boxX, y: 3.1, w: 2.2, h: 0.5, fontSize: 16, bold: true, color: m.color, align: 'center' });
    });
    
    // Competitive advantage (legacy reports) or MECE note (new reports)
    if (report.financial_impact_summary.competitive_advantage_value) {
      slide7.addShape(pres.ShapeType.roundRect, { x: 0.5, y: 3.9, w: 9, h: 0.7, fill: { color: 'EEF2FF' }, rectRadius: 0.1 });
      slide7.addText('Competitive Advantage Value', { x: 0.5, y: 3.95, w: 4, h: 0.3, fontSize: 10, color: COLORS.textLight, align: 'center' });
      slide7.addText(report.financial_impact_summary.competitive_advantage_value, { x: 4.5, y: 3.95, w: 5, h: 0.6, fontSize: 20, bold: true, color: COLORS.indigo, align: 'center' });
    } else if (report.financial_impact_summary.mece_note || report.financial_impact_summary.reconciliation_note) {
      slide7.addText(report.financial_impact_summary.reconciliation_note || report.financial_impact_summary.mece_note, { x: 0.5, y: 3.95, w: 9, h: 0.8, fontSize: 9, italic: true, color: COLORS.textLight, align: 'center' });
    }
  }

  // ===== SLIDE 8: ROI Analysis =====
  if (report?.roi_analysis) {
    const slide8 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide8.addText('ROI Analysis', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide8.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    const roiMetrics = [
      { label: 'Year 1 ROI', value: report.roi_analysis.year_1_roi, color: COLORS.green, bgColor: 'ECFDF5' },
      { label: 'Year 3 ROI', value: report.roi_analysis.year_3_roi, color: COLORS.blue, bgColor: 'EFF6FF' },
      { label: 'Payback Period', value: report.roi_analysis.payback_period, color: COLORS.purple, bgColor: 'FAF5FF' },
      { label: 'Net Present Value', value: report.roi_analysis.net_present_value, color: COLORS.amber, bgColor: 'FFFBEB' },
    ];
    
    roiMetrics.forEach((m, idx) => {
      const boxX = 0.5 + idx * 2.35;
      slide8.addShape(pres.ShapeType.roundRect, { x: boxX, y: 1.1, w: 2.2, h: 1.2, fill: { color: m.bgColor }, rectRadius: 0.1 });
      slide8.addText(m.label, { x: boxX, y: 1.2, w: 2.2, h: 0.3, fontSize: 10, color: COLORS.textLight, align: 'center' });
      slide8.addText(m.value || 'N/A', { x: boxX, y: 1.55, w: 2.2, h: 0.6, fontSize: 22, bold: true, color: m.color, align: 'center' });
    });
    
    // Annual projections
    slide8.addShape(pres.ShapeType.roundRect, { x: 0.5, y: 2.6, w: 9, h: 1.4, fill: { color: COLORS.lightGray }, rectRadius: 0.1 });
    slide8.addText('Annual Financial Projections', { x: 0.5, y: 2.7, w: 9, h: 0.4, fontSize: 14, bold: true, color: COLORS.text, align: 'center' });
    
    const yearValues = [
      { year: 'Year 1', value: report.roi_analysis.year_1_value },
      { year: 'Year 2', value: report.roi_analysis.year_2_value },
      { year: 'Year 3', value: report.roi_analysis.year_3_value },
    ];
    
    yearValues.forEach((y, idx) => {
      const boxX = 1.5 + idx * 2.5;
      slide8.addText(y.year, { x: boxX, y: 3.1, w: 2, h: 0.25, fontSize: 10, color: COLORS.textLight, align: 'center' });
      slide8.addText(y.value || 'N/A', { x: boxX, y: 3.35, w: 2, h: 0.5, fontSize: 20, bold: true, color: COLORS.text, align: 'center' });
    });

    // Scenario analysis + reconciliation (audit-hardening)
    if (report?.scenario_analysis) {
      const scen = report.scenario_analysis;
      slide8.addText('Scenario Analysis (3-year value)', { x: 0.5, y: 4.05, w: 9, h: 0.25, fontSize: 11, bold: true, color: COLORS.text });
      [{ label: 'Conservative', value: scen.conservative, color: COLORS.textLight },
       { label: 'Base', value: scen.base, color: COLORS.primary },
       { label: 'Stretch', value: scen.stretch, color: COLORS.blue }].forEach((s, idx) => {
        const bx = 0.5 + idx * 3.1;
        slide8.addShape(pres.ShapeType.roundRect, { x: bx, y: 4.35, w: 2.9, h: 0.55, fill: { color: COLORS.lightGray }, rectRadius: 0.08 });
        slide8.addText(s.label, { x: bx + 0.15, y: 4.35, w: 1.4, h: 0.55, fontSize: 9, color: COLORS.textLight, valign: 'middle' });
        slide8.addText(s.value || '—', { x: bx + 1.3, y: 4.35, w: 1.45, h: 0.55, fontSize: 13, bold: true, color: s.color, align: 'right', valign: 'middle' });
      });
    }
    if (report.roi_analysis.reconciliation) {
      slide8.addText(`Reconciliation: ${report.roi_analysis.reconciliation}`.substring(0, 230), { x: 0.5, y: 5.0, w: 9, h: 0.5, fontSize: 8, italic: true, color: COLORS.textLight, wrap: true });
    }
  }

  // ===== SLIDE 9: Business Value Realization =====
  if (report?.business_value_realization) {
    const slide9 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide9.addText('Business Value Realization Timeline', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide9.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    const bvr = report.business_value_realization;
    const timelineItems = [
      { title: 'Quick Wins', period: '0-6 months', desc: bvr.quick_wins_description, value: bvr.quick_wins_value, color: COLORS.yellow, bgColor: 'FFFBEB' },
      { title: 'Medium Term', period: '6-18 months', desc: bvr.medium_term_description, value: bvr.medium_term_value, color: COLORS.blue, bgColor: 'EFF6FF' },
      { title: 'Long Term', period: '18-36 months', desc: bvr.long_term_description, value: bvr.long_term_value, color: COLORS.green, bgColor: 'ECFDF5' },
    ];
    
    timelineItems.forEach((item, idx) => {
      const boxX = 0.5 + idx * 3.1;
      slide9.addShape(pres.ShapeType.roundRect, { x: boxX, y: 1.1, w: 2.9, h: 2.8, fill: { color: item.bgColor }, rectRadius: 0.1 });
      slide9.addText(item.title, { x: boxX + 0.1, y: 1.2, w: 2.7, h: 0.35, fontSize: 14, bold: true, color: COLORS.text });
      slide9.addText(item.period, { x: boxX + 0.1, y: 1.5, w: 2.7, h: 0.25, fontSize: 9, color: COLORS.textLight });
      slide9.addText(item.desc || 'Description not available', { x: boxX + 0.1, y: 1.85, w: 2.7, h: 1.3, fontSize: 9, color: COLORS.text, valign: 'top', wrap: true });
      slide9.addText(item.value || 'N/A', { x: boxX + 0.1, y: 3.4, w: 2.7, h: 0.4, fontSize: 18, bold: true, color: item.color });
    });
  }

  // ===== SLIDE 10: 5-Year Strategic Roadmap =====
  if (fiveYearRoadmap.length > 0) {
    const slide10 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide10.addText('5-Year Strategic Roadmap', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide10.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    fiveYearRoadmap.slice(0, 5).forEach((year, idx) => {
      const boxY = 1.1 + idx * 0.75;
      // Year label
      slide10.addShape(pres.ShapeType.roundRect, { x: 0.5, y: boxY, w: 0.8, h: 0.6, fill: { color: COLORS.purple }, rectRadius: 0.05 });
      slide10.addText(`Year ${idx + 1}`, { x: 0.5, y: boxY, w: 0.8, h: 0.6, fontSize: 10, bold: true, color: COLORS.white, align: 'center', valign: 'middle' });
      // Focus
      slide10.addText(year.focus || 'Focus area', { x: 1.4, y: boxY, w: 3.5, h: 0.35, fontSize: 11, bold: true, color: COLORS.text });
      // Goals (abbreviated)
      const goalsText = year.goals?.slice(0, 2).join('; ') || 'Goals to be defined';
      slide10.addText(goalsText, { x: 1.4, y: boxY + 0.28, w: 5.5, h: 0.2, fontSize: 8, color: COLORS.textLight });
      // Decision gate (audit-hardening)
      if (year.decision_gate) {
        slide10.addText(`Gate: ${year.decision_gate}`.substring(0, 120), { x: 1.4, y: boxY + 0.5, w: 6.6, h: 0.18, fontSize: 7, italic: true, color: COLORS.primary });
      }
      // Investment
      if (year.estimated_investment) {
        slide10.addShape(pres.ShapeType.roundRect, { x: 8.2, y: boxY + 0.1, w: 1.3, h: 0.4, fill: { color: 'EDE9FE' }, rectRadius: 0.05 });
        slide10.addText(year.estimated_investment, { x: 8.2, y: boxY + 0.1, w: 1.3, h: 0.4, fontSize: 9, color: COLORS.purple, align: 'center', valign: 'middle' });
      }
    });
  }

  // ===== SLIDE 11: Value Creation Framework (EOAI) =====
  const slide11 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
  slide11.addText('Value Creation Framework', { x: 0.5, y: 0.3, w: 9, h: 0.5, fontSize: 28, bold: true, color: COLORS.text });
  slide11.addText('Eliminate • Optimize • Automate • Industrialize (EOAI)', { x: 0.5, y: 0.75, w: 9, h: 0.3, fontSize: 11, color: COLORS.textLight });
  
  const eoaiItems = [
    { title: 'Eliminate', items: report?.eliminate || [], color: COLORS.red, bgColor: 'FEF2F2' },
    { title: 'Optimize', items: report?.optimize || [], color: COLORS.amber, bgColor: 'FFFBEB' },
    { title: 'Automate', items: report?.automate || [], color: COLORS.blue, bgColor: 'EFF6FF' },
    { title: 'Industrialize', items: report?.industrialize || [], color: COLORS.purple, bgColor: 'FAF5FF' },
  ];
  
  eoaiItems.forEach((eoai, idx) => {
    const boxX = 0.5 + idx * 2.35;
    slide11.addShape(pres.ShapeType.roundRect, { x: boxX, y: 1.1, w: 2.2, h: 3.5, fill: { color: eoai.bgColor }, rectRadius: 0.1 });
    slide11.addText(eoai.title, { x: boxX, y: 1.2, w: 2.2, h: 0.4, fontSize: 14, bold: true, color: eoai.color, align: 'center' });
    
    // List items
    eoai.items.slice(0, 3).forEach((item, itemIdx) => {
      slide11.addText(`• ${item.title || 'Item'}`, { x: boxX + 0.1, y: 1.7 + itemIdx * 0.9, w: 2, h: 0.25, fontSize: 9, bold: true, color: COLORS.text });
      slide11.addText(item.description?.substring(0, 80) || '', { x: boxX + 0.1, y: 1.95 + itemIdx * 0.9, w: 2, h: 0.5, fontSize: 7, color: COLORS.textLight, wrap: true });
    });
  });

  // ===== SLIDE 12: Investment & Resources =====
  if (report?.total_investment || report?.investment_breakdown?.length > 0) {
    const slide12 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide12.addText('Investment & Resources', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide12.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    if (report?.total_investment) {
      slide12.addShape(pres.ShapeType.roundRect, { x: 2, y: 1.1, w: 6, h: 1, fill: { color: COLORS.indigo }, rectRadius: 0.15 });
      slide12.addText('TOTAL INVESTMENT REQUIRED', { x: 2, y: 1.2, w: 6, h: 0.25, fontSize: 9, color: COLORS.white, align: 'center', transparency: 20 });
      slide12.addText(report.total_investment, { x: 2, y: 1.45, w: 6, h: 0.55, fontSize: 28, bold: true, color: COLORS.white, align: 'center' });
    }
    
    if (report?.investment_breakdown?.length > 0) {
      report.investment_breakdown.slice(0, 6).forEach((item, idx) => {
        const row = Math.floor(idx / 3);
        const col = idx % 3;
        const boxX = 0.5 + col * 3.1;
        const boxY = 2.3 + row * 1.1;
        
        slide12.addShape(pres.ShapeType.roundRect, { x: boxX, y: boxY, w: 2.9, h: 1, fill: { color: COLORS.lightGray }, rectRadius: 0.1 });
        slide12.addText(item.area, { x: boxX + 0.1, y: boxY + 0.1, w: 2.7, h: 0.25, fontSize: 9, bold: true, color: COLORS.text });
        slide12.addText(item.amount, { x: boxX + 0.1, y: boxY + 0.35, w: 1.5, h: 0.3, fontSize: 14, bold: true, color: COLORS.indigo });
        slide12.addText(item.percentage || '', { x: boxX + 1.7, y: boxY + 0.4, w: 1.1, h: 0.2, fontSize: 8, color: COLORS.textLight });
      });
    }
  }

  // ===== SLIDE 13: Key Recommendations =====
  if (areasOfImprovement.length > 0) {
    const slide13 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide13.addText('Key Recommendations', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide13.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    areasOfImprovement.slice(0, 6).forEach((area, idx) => {
      const row = Math.floor(idx / 3);
      const col = idx % 3;
      const boxX = 0.5 + col * 3.1;
      const boxY = 1.1 + row * 1.6;
      
      const priorityColor = area.priority === 'High' ? COLORS.red : area.priority === 'Medium' ? COLORS.amber : COLORS.textLight;
      const bgColor = area.priority === 'High' ? 'FEF2F2' : area.priority === 'Medium' ? 'FFFBEB' : COLORS.lightGray;
      
      slide13.addShape(pres.ShapeType.roundRect, { x: boxX, y: boxY, w: 2.9, h: 1.4, fill: { color: bgColor }, rectRadius: 0.1 });
      slide13.addShape(pres.ShapeType.rect, { x: boxX, y: boxY, w: 0.08, h: 1.4, fill: { color: priorityColor } });
      
      slide13.addText(area.area, { x: boxX + 0.15, y: boxY + 0.1, w: 2.3, h: 0.35, fontSize: 10, bold: true, color: COLORS.text });
      slide13.addText(area.priority, { x: boxX + 2.1, y: boxY + 0.1, w: 0.7, h: 0.25, fontSize: 8, color: priorityColor, align: 'right' });
      slide13.addText(area.potentialImpact, { x: boxX + 0.15, y: boxY + 0.45, w: 2.6, h: 0.25, fontSize: 9, color: COLORS.primary });
      if (area.timeline) {
        slide13.addText(area.timeline, { x: boxX + 0.15, y: boxY + 0.7, w: 2.6, h: 0.2, fontSize: 8, color: COLORS.textLight });
      }
    });
  }

  // ===== SLIDE 14: Case Studies =====
  if (report?.case_studies?.length > 0) {
    const slide14 = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slide14.addText('Industry Case Studies', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slide14.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    
    report.case_studies.slice(0, 2).forEach((cs, idx) => {
      const boxY = 1.1 + idx * 1.9;
      
      slide14.addShape(pres.ShapeType.roundRect, { x: 0.5, y: boxY, w: 9, h: 1.7, fill: { color: 'FFF7ED' }, rectRadius: 0.1 });
      
      // Company logo placeholder
      slide14.addShape(pres.ShapeType.roundRect, { x: 0.7, y: boxY + 0.2, w: 0.6, h: 0.6, fill: { color: COLORS.orange }, rectRadius: 0.05 });
      const companyInitial = safeStr(cs.company, 1) || 'C';
      slide14.addText(companyInitial, { x: 0.7, y: boxY + 0.2, w: 0.6, h: 0.6, fontSize: 18, bold: true, color: COLORS.white, align: 'center', valign: 'middle' });
      
      slide14.addText(safeStr(cs.company, 50) || 'Company', { x: 1.4, y: boxY + 0.2, w: 3, h: 0.3, fontSize: 12, bold: true, color: COLORS.text });
      slide14.addText(safeStr(cs.industry, 50) || 'Industry', { x: 1.4, y: boxY + 0.5, w: 3, h: 0.25, fontSize: 9, color: COLORS.orange });
      
      slide14.addText('Challenge:', { x: 0.7, y: boxY + 0.9, w: 1, h: 0.2, fontSize: 8, bold: true, color: COLORS.red });
      slide14.addText(safeStr(cs.challenge, 100) || 'N/A', { x: 1.7, y: boxY + 0.9, w: 7.6, h: 0.2, fontSize: 8, color: COLORS.textLight });
      
      slide14.addText('Solution:', { x: 0.7, y: boxY + 1.15, w: 1, h: 0.2, fontSize: 8, bold: true, color: COLORS.blue });
      slide14.addText(safeStr(cs.solution, 100) || 'N/A', { x: 1.7, y: boxY + 1.15, w: 7.6, h: 0.2, fontSize: 8, color: COLORS.textLight });
      
      slide14.addText('Results:', { x: 0.7, y: boxY + 1.4, w: 1, h: 0.2, fontSize: 8, bold: true, color: COLORS.primary });
      slide14.addText(safeStr(cs.results, 100) || 'N/A', { x: 1.7, y: boxY + 1.4, w: 7.6, h: 0.2, fontSize: 8, bold: true, color: COLORS.text });
    });
  }

  // ===== SLIDE: Methodology, Evidence & Limitations (audit-hardening) =====
  const gov = analysis?.data_governance;
  if (gov || report?.confidence_statement) {
    const slideGov = pres.addSlide({ masterName: 'SAMANVAI_MASTER' });
    slideGov.addText('Methodology, Evidence & Limitations', { x: 0.5, y: 0.3, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.text });
    slideGov.addShape(pres.ShapeType.rect, { x: 0.5, y: 0.85, w: 0.8, h: 0.06, fill: { color: COLORS.primary } });
    slideGov.addText('AI-generated opportunity hypothesis — not an independent assessment, audit or assurance opinion. Every figure is classified on the evidence ladder below and requires management validation before decisions.', { x: 0.5, y: 1.0, w: 9, h: 0.4, fontSize: 9.5, italic: true, color: COLORS.textLight, wrap: true });

    const ladder = [
      { label: 'Reported', desc: 'Directly from company disclosure', color: COLORS.green },
      { label: 'Derived', desc: 'Calculated from disclosed data', color: COLORS.blue },
      { label: 'Estimated', desc: 'Assumption-dependent, sector benchmarks', color: COLORS.amber },
      { label: 'Modelled', desc: 'Scenario output of the value model', color: COLORS.purple },
    ];
    ladder.forEach((l, idx) => {
      const bx = 0.5 + idx * 2.35;
      slideGov.addShape(pres.ShapeType.roundRect, { x: bx, y: 1.5, w: 2.2, h: 0.85, fill: { color: COLORS.lightGray }, rectRadius: 0.08 });
      slideGov.addText(l.label, { x: bx + 0.1, y: 1.57, w: 2, h: 0.25, fontSize: 11, bold: true, color: l.color });
      slideGov.addText(l.desc, { x: bx + 0.1, y: 1.85, w: 2, h: 0.45, fontSize: 8, color: COLORS.textLight, wrap: true });
    });

    let gy = 2.55;
    if (gov?.reporting_period_used) {
      slideGov.addText(`Reporting period used: ${gov.reporting_period_used}${gov.data_current_as_of ? ` · Data current as of ${gov.data_current_as_of}` : ''}${gov.period_requested ? ` · Requested: ${gov.period_requested}` : ''}${gov.report_generated_on ? ` · Generated on ${gov.report_generated_on}` : ''}`, { x: 0.5, y: gy, w: 9, h: 0.25, fontSize: 10, bold: true, color: COLORS.text });
      gy += 0.3;
    }
    if (gov?.freshness_note) {
      slideGov.addText(`Freshness: ${gov.freshness_note}`.substring(0, 190), { x: 0.5, y: gy, w: 9, h: 0.25, fontSize: 9, color: COLORS.textLight });
      gy += 0.3;
    }
    const lims = Array.isArray(gov?.limitations) ? gov.limitations.slice(0, 4) : [];
    if (lims.length > 0) {
      slideGov.addText('Limitations', { x: 0.5, y: gy, w: 9, h: 0.25, fontSize: 11, bold: true, color: COLORS.text });
      gy += 0.28;
      lims.forEach((lim) => {
        slideGov.addText(`• ${String(lim).substring(0, 145)}`, { x: 0.6, y: gy, w: 8.9, h: 0.24, fontSize: 8.5, color: COLORS.textLight });
        gy += 0.26;
      });
    }
    if (report?.confidence_statement) {
      slideGov.addText(`Confidence: ${report.confidence_statement}`.substring(0, 280), { x: 0.5, y: Math.min(gy + 0.1, 4.55), w: 9, h: 0.65, fontSize: 8.5, italic: true, color: COLORS.textLight, wrap: true });
    }
  }

  // ===== SLIDE 15: Summary & Next Steps =====
  const slideFinal = pres.addSlide();
  slideFinal.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: '100%', fill: { color: '111827' } }); // gray-900
  
  slideFinal.addText('Summary & Next Steps', { x: 0.5, y: 0.5, w: 9, h: 0.6, fontSize: 28, bold: true, color: COLORS.white });
  
  // Key Takeaways
  slideFinal.addText('Key Takeaways', { x: 0.5, y: 1.2, w: 4, h: 0.4, fontSize: 14, bold: true, color: '34D399' }); // emerald-400
  
  const takeaways = [
    `${kpiData.length} KPIs analyzed - ${kpisBelowAvg} need attention`,
    `${formatCurrency(totalPotentialImpact, CUR)} potential from 1% improvement`,
    report?.financial_impact_summary?.total_business_value ? `${report.financial_impact_summary.total_business_value} total value over 3 years` : 'Significant value potential identified',
  ];
  
  takeaways.forEach((t, idx) => {
    slideFinal.addText(`✓  ${t}`, { x: 0.5, y: 1.7 + idx * 0.4, w: 4.5, h: 0.35, fontSize: 11, color: 'D1D5DB' }); // gray-300
  });
  
  // Next Steps
  slideFinal.addText('Next Steps', { x: 5.2, y: 1.2, w: 4, h: 0.4, fontSize: 14, bold: true, color: '34D399' });
  
  const nextSteps = [
    'Schedule executive workshop',
    'Prioritize quick wins',
    'Develop implementation roadmap',
  ];
  
  nextSteps.forEach((step, idx) => {
    slideFinal.addShape(pres.ShapeType.ellipse, { x: 5.2, y: 1.75 + idx * 0.4, w: 0.25, h: 0.25, fill: { color: COLORS.primary } });
    slideFinal.addText((idx + 1).toString(), { x: 5.2, y: 1.75 + idx * 0.4, w: 0.25, h: 0.25, fontSize: 9, bold: true, color: COLORS.white, align: 'center', valign: 'middle' });
    slideFinal.addText(step, { x: 5.55, y: 1.73 + idx * 0.4, w: 4, h: 0.3, fontSize: 11, color: 'D1D5DB' });
  });
  
  // Footer
  slideFinal.addShape(pres.ShapeType.rect, { x: 0, y: 4.3, w: '100%', h: 0.01, fill: { color: '374151' } }); // gray-700
  
  slideFinal.addShape(pres.ShapeType.roundRect, { x: 0.5, y: 4.5, w: 0.5, h: 0.5, fill: { color: COLORS.primary }, rectRadius: 0.08 });
  slideFinal.addText('S', { x: 0.5, y: 4.5, w: 0.5, h: 0.5, fontSize: 16, bold: true, color: COLORS.white, align: 'center', valign: 'middle' });
  slideFinal.addText('SAMANVAI', { x: 1.1, y: 4.6, w: 1.5, h: 0.3, fontSize: 12, bold: true, color: COLORS.white });
  
  slideFinal.addText('contact@samanvai.com', { x: 7.5, y: 4.65, w: 2, h: 0.2, fontSize: 9, color: '9CA3AF', align: 'right' }); // gray-400

  // ===== SLIDE 16: References & Sources =====
  if (Array.isArray(report?.references) && report.references.length > 0) {
    const refs = report.references;

    // Group references by category preserving display order
    const ORDER = ['Public Disclosure', 'Industry Benchmark', 'Market Research', 'News / Analyst', 'Regulatory / Standards', 'Methodology'];
    const grouped = {};
    refs.forEach((r) => {
      const k = (r && r.category) || 'Other';
      if (!grouped[k]) grouped[k] = [];
      grouped[k].push(r);
    });
    const cats = Object.keys(grouped).sort((a, b) => {
      const ia = ORDER.indexOf(a); const ib = ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

    const slideRefs = pres.addSlide();
    slideRefs.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: '100%', fill: { color: 'FFFFFF' } });

    slideRefs.addText('References & Sources', {
      x: 0.5, y: 0.4, w: 9, h: 0.5, fontSize: 24, bold: true, color: '111827'
    });
    slideRefs.addShape(pres.ShapeType.rect, { x: 0.5, y: 1.0, w: 0.5, h: 0.06, fill: { color: COLORS.primary } });
    slideRefs.addText(
      'The Value Scorecard is anchored in the public disclosures, industry benchmarks and methodologies below. Each figure can be traced back to one or more of these sources.',
      { x: 0.5, y: 1.1, w: 9, h: 0.5, fontSize: 10, italic: true, color: '6B7280' }
    );

    // Two-column flowing layout
    const colWidth = 4.2;
    const xLeft = 0.5;
    const xRight = 5.3;
    const yStart = 1.7;
    const lineH = 0.18;
    const blockGap = 0.18;
    let yL = yStart, yR = yStart;

    const CATEGORY_COLOR = {
      'Public Disclosure':       '10B981',
      'Industry Benchmark':      '3B82F6',
      'Market Research':         '8B5CF6',
      'News / Analyst':          'F43F5E',
      'Regulatory / Standards':  'F59E0B',
      'Methodology':             '64748B',
      'Other':                   '9CA3AF',
    };

    cats.forEach((cat) => {
      const items = grouped[cat];
      const color = CATEGORY_COLOR[cat] || CATEGORY_COLOR['Other'];

      // Estimate block height: 0.28 for header + 0.42 per item (max 2 lines)
      const blockH = 0.3 + items.length * 0.42 + blockGap;

      // Choose column with more space
      const targetX = yL <= yR ? xLeft : xRight;
      let y = targetX === xLeft ? yL : yR;

      // Pagination guard — if a block runs off-slide, stop (slide has h=5.625"; safe zone ~4.9")
      if (y + blockH > 5.1) return;

      // Category accent dot + label
      slideRefs.addShape(pres.ShapeType.ellipse, { x: targetX, y: y + 0.04, w: 0.1, h: 0.1, fill: { color: color } });
      slideRefs.addText(cat.toUpperCase(), {
        x: targetX + 0.18, y: y, w: colWidth - 0.18, h: 0.22,
        fontSize: 9, bold: true, color, charSpacing: 2,
      });
      y += 0.3;

      items.forEach((r) => {
        const titleStr = r.title || 'Reference';
        const meta = [r.publisher, r.year].filter(Boolean).join(' · ');
        slideRefs.addText(`• ${titleStr}`, {
          x: targetX, y: y, w: colWidth, h: lineH,
          fontSize: 9.5, bold: true, color: '1F2937',
        });
        if (meta) {
          slideRefs.addText(meta, {
            x: targetX + 0.12, y: y + lineH, w: colWidth - 0.12, h: lineH,
            fontSize: 8.5, color: '6B7280',
          });
        }
        if (r.note) {
          slideRefs.addText(r.note, {
            x: targetX + 0.12, y: y + lineH * 2, w: colWidth - 0.12, h: lineH,
            fontSize: 8, italic: true, color: '9CA3AF',
          });
        }
        y += 0.42;
      });
      y += blockGap;

      if (targetX === xLeft) yL = y; else yR = y;
    });

    // Footer
    slideRefs.addShape(pres.ShapeType.rect, { x: 0, y: 5.2, w: '100%', h: 0.01, fill: { color: 'E5E7EB' } });
    slideRefs.addText('Generated by SAMANVAI · Citations provided for transparency and audit', {
      x: 0.5, y: 5.25, w: 9, h: 0.3, fontSize: 8, italic: true, color: '9CA3AF', align: 'center',
    });
  }

  // Download the file
  appendQualitySlide(pres, report);
  await pres.writeFile({ fileName: `${companyName.replace(/[^a-zA-Z0-9]/g, '_')}_Value_Scorecard.pptx` });
  
  return true;
};

export default generatePptx;
